import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PlayerContext } from "../contexts/PlayerContext";
import { CurrentPlaylistContext } from "../contexts/CurrentPlaylistContext";
import quotes from "../data/quotes.json";
import AmbientSoundPanel from "./AmbientSoundPanel";
import { AmbientSoundContext, TONE_INFO } from "../contexts/AmbientSoundContext";

// Import meditation background images
import bg1 from "../data/meditace/cross-3983452_1920.jpg";
import bg2 from "../data/meditace/cross-6464122_1920.jpg";
import bg3 from "../data/meditace/crosses-2713356_1280.jpg";
import bg4 from "../data/meditace/landscape-7396852_1920.jpg";
import bg5 from "../data/meditace/mosaic-4385381_1920.jpg";
import bg6 from "../data/meditace/passion-1138007_1280.jpg";
import bg7 from "../data/meditace/aaron-burden-09AhDCedXF8-unsplash.jpg";
import bg8 from "../data/meditace/aaron-burden-535Npq1wFG8-unsplash.jpg";
import bg9 from "../data/meditace/aaron-burden-vKBdY7e7KFk-unsplash.jpg";
import bg10 from "../data/meditace/arturo-rey-CvnVk7GGQCg-unsplash.jpg";
import bg11 from "../data/meditace/chad-greiter--0gBnnMdQPw-unsplash.jpg";
import bg12 from "../data/meditace/gabriel-lamza-yJr1rbbrAGw-unsplash.jpg";
import bg13 from "../data/meditace/john-cafazza-AeABkasP-24-unsplash.jpg";
import bg14 from "../data/meditace/john-price-RAZQiZOX3mU-unsplash.jpg";
import bg15 from "../data/meditace/karl-fredrickson-JRsZWmRd_Ws-unsplash.jpg";
import bg16 from "../data/meditace/nathan-dumlao-k-oS0iKn0Qg-unsplash.jpg";
import bg17 from "../data/meditace/samuel-lopes-Ip5pvMPwlyo-unsplash.jpg";
import bg18 from "../data/meditace/samuel-mcgarrigle-GVRRtaLj3LU-unsplash.jpg";
import bg19 from "../data/meditace/zachary-olson-vEQe_DuVmKk-unsplash.jpg";

const backgroundImages = [bg1, bg2, bg3, bg4, bg5, bg6, bg7, bg8, bg9, bg10, bg11, bg12, bg13, bg14, bg15, bg16, bg17, bg18, bg19];

const MeditationMode = () => {
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [visibleWords, setVisibleWords] = useState(0);

  const { isPlaying, playerRef } = useContext(PlayerContext);
  const { activeVideos, activeVideoIndex, setActiveVideoIndex } =
    useContext(CurrentPlaylistContext);
  const { activeSound, setMainPlayer } = useContext(AmbientSoundContext);

  // Pass main player ref to ambient context
  React.useEffect(() => {
    if (playerRef.current) {
      setMainPlayer(playerRef.current);
    }
  }, [playerRef, setMainPlayer]);

  // Get current song info from active (playing) state
  const currentVideo =
    activeVideos.length > 0 && activeVideoIndex >= 0
      ? activeVideos[activeVideoIndex]
      : null;

  // Auto-rotate quotes and backgrounds every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);

      setTimeout(() => {
        setCurrentQuoteIndex((prevIndex) => (prevIndex + 1) % quotes.length);
        setCurrentBgIndex(
          (prevIndex) => (prevIndex + 1) % backgroundImages.length
        );
        setIsVisible(true);
      }, 500); // Wait for fade out before changing quote
    }, 30000); // Change every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const currentQuote = quotes[currentQuoteIndex];

  // Letter-by-letter fade in effect
  const letters = currentQuote.text.split("");
  const totalLetters = letters.length;

  useEffect(() => {
    if (!isVisible) {
      setVisibleWords(0); // reusing state for letters count
      return;
    }

    setVisibleWords(0);
    let letterCount = 0;

    const letterInterval = setInterval(() => {
      letterCount++;
      if (letterCount <= totalLetters) {
        setVisibleWords(letterCount);
      } else {
        clearInterval(letterInterval);
      }
    }, 110); // 130ms per letter - gentle meditation pace

    return () => clearInterval(letterInterval);
  }, [currentQuoteIndex, isVisible, totalLetters]);

  const playNextVideo = () => {
    const nextIndex = (activeVideoIndex + 1) % activeVideos.length;
    const nextVideo = activeVideos[nextIndex];
    if (!nextVideo || !playerRef.current) {
      setActiveVideoIndex(nextIndex);
      return;
    }
    if (playerRef.current.loadVideoById) {
      playerRef.current.loadVideoById({
        videoId: nextVideo.video_id,
        startSeconds: 0,
      });
    }
    setActiveVideoIndex(nextIndex);
  };

  const playPreviousVideo = () => {
    const prevIndex = (activeVideoIndex - 1 + activeVideos.length) % activeVideos.length;
    const prevVideo = activeVideos[prevIndex];
    if (!prevVideo || !playerRef.current) {
      setActiveVideoIndex(prevIndex);
      return;
    }
    if (playerRef.current.loadVideoById) {
      playerRef.current.loadVideoById({
        videoId: prevVideo.video_id,
        startSeconds: 0,
      });
    }
    setActiveVideoIndex(prevIndex);
  };

  const togglePlayPause = () => {
    if (!playerRef.current) return;
    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  };

  return (
    <div className="meditation-mode">
      {/* Background image with transition */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentBgIndex}
          className="meditation-bg"
          style={{
            backgroundImage: `url(${backgroundImages[currentBgIndex]})`,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
        />
      </AnimatePresence>
      <div className="meditation-overlay" />
      <div className="meditation-content">
        {/* Quote container - positioned at bottom right */}
        <div className="quote-wrapper">
          <AnimatePresence mode="wait">
            {isVisible && (
              <motion.div
                key={currentQuoteIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="quote-container">
                  <p className="quote-text">
                    <span
                      style={{
                        opacity: visibleWords > 0 ? 1 : 0,
                        transition: "opacity 0.5s ease-in-out",
                      }}
                    >
                      "
                    </span>
                    {letters.map((letter, index) => (
                      <span
                        key={index}
                        style={{
                          opacity: index < visibleWords ? 1 : 0,
                          transition: "opacity 0.5s ease-in-out",
                        }}
                      >
                        {letter}
                      </span>
                    ))}
                    <span
                      style={{
                        opacity: visibleWords >= totalLetters ? 1 : 0,
                        transition: "opacity 0.5s ease-in-out",
                      }}
                    >
                      "
                    </span>
                  </p>
                  <p
                    className="quote-reference"
                    style={{
                      opacity: visibleWords >= totalLetters ? 1 : 0,
                      transition: "opacity 0.5s ease-in-out",
                    }}
                  >
                    {currentQuote.reference}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Show playing indicator and controls - fixed at bottom */}
        {(currentVideo || activeSound === "tone") && (
          <div
            className="playing-indicator"
            style={{ flexDirection: "column", gap: "2rem", flexShrink: 0 }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              {(isPlaying || activeSound === "tone") && (
                <div className="music-wave">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              )}
              <p className="now-playing">
                {activeSound === "tone"
                  ? `${TONE_INFO.artist} - ${TONE_INFO.title}`
                  : `${currentVideo.artist} - ${currentVideo.title}`}
              </p>
            </div>

            {/* Player controls - hidden when tone is playing */}
            {activeSound !== "tone" && (
              <div
                style={{
                  display: "flex",
                  gap: "2rem",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="fa-solid fa-backward"
                  onClick={playPreviousVideo}
                  style={{
                    fontSize: "2rem",
                    cursor: "pointer",
                    color: "rgba(255, 255, 255, 0.9)",
                  }}
                ></i>
                <i
                  className={`fa-solid ${isPlaying ? "fa-pause" : "fa-play"}`}
                  onClick={togglePlayPause}
                  style={{
                    fontSize: "2.5rem",
                    cursor: "pointer",
                    color: "rgba(255, 255, 255, 0.9)",
                  }}
                ></i>
                <i
                  className="fa-solid fa-forward"
                  onClick={playNextVideo}
                  style={{
                    fontSize: "2rem",
                    cursor: "pointer",
                    color: "rgba(255, 255, 255, 0.9)",
                  }}
                ></i>
              </div>
            )}
          </div>
        )}
      </div>

      <AmbientSoundPanel />
    </div>
  );
};

export default MeditationMode;
